import './settingsModal.css';
import React, { useState, useRef, useEffect } from 'react';
import IconTooltip from '../../../common/icon_tooltip/iconTooltip';
import { Settings } from 'lucide-react';
import { useApiData } from '../../../../contexts/apiService';
import { useDataManager } from '../../../../contexts/dataManager';
import { downloadT1 } from './settings_options/t1_form_export/excelT1Download';
import ReportUploader, { ReportUploaderHandle } from './settings_options/carrier_report_uploader/reportUploader';
import PdfUploader, { PdfUploaderHandle } from './settings_options/pdf_uploader/pdfUploader';
import AutomaticScheduling from './settings_options/automatic_scheduling_panel/AutomaticScheduling';

interface SettingsModalProps {
    finalTableData?: Record<string, any>[];
    hasActiveFilters?: boolean;
    currentMonthFilter?: string;
    tableName: string;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
    finalTableData,
    hasActiveFilters,
    currentMonthFilter,
    tableName,
}) => {
    
    const { apiData } = useApiData();
    const { requestTableData, hasTableData } = useDataManager();
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
    const settingsIconRef = useRef<HTMLDivElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const reportUploaderRef = useRef<ReportUploaderHandle>(null);
    const pdfUploaderRef = useRef<PdfUploaderHandle>(null);

    // Calculate modal position when it opens
    useEffect(() => {
        if (showSettingsModal && settingsIconRef.current) {
            const iconRect = settingsIconRef.current.getBoundingClientRect();

            let top = iconRect.bottom + 8; // 8px gap below icon
            let left = iconRect.left; // Distance from viewport right edge to icon left edge

            setModalPosition({ top, left });
        }
    }, [showSettingsModal]);

    const handleShowSettings = () => {
        setShowSettingsModal(!showSettingsModal);
    };

    // Close modal when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(event.target as Node) &&
                settingsIconRef.current && !settingsIconRef.current.contains(event.target as Node)) {
                setShowSettingsModal(false);
            }
        };

        if (showSettingsModal) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showSettingsModal]);
    
    const handleDownloadT1 = async () => {
        if (!hasTableData('lease_coordinates')) {
            await requestTableData('lease_coordinates');
        }
        if (!hasTableData('market_coordinates')) {
            await requestTableData('market_coordinates');
        }
        downloadT1(finalTableData ?? [], apiData, currentMonthFilter);
        setShowSettingsModal(false);
    };

    const handleOpenReportUploader = () => {
        reportUploaderRef.current?.openModal();
        setShowSettingsModal(false);
    };

    const handleOpenPdfUploader = () => {
        pdfUploaderRef.current?.openModal();
        setShowSettingsModal(false);
    };

    const [showAutomaticScheduling, setShowAutomaticScheduling] = useState(false);

    return (
        <div className="settingsModal-container">
            <div ref={settingsIconRef}>
                <IconTooltip 
                    icon={Settings}
                    onClick={handleShowSettings}
                    showTooltip={true}
                    label="Settings"
                    position="top"
                    iconColor={showSettingsModal ? 'var(--color-blue-default)' : 'var(--color-grey-light-text)'}
                    isActive={showSettingsModal}
                />
            </div>
            
            {showSettingsModal && (
                <div 
                    ref={modalRef}
                    className="settingsModal-modal"
                    style={{
                            top: `${modalPosition.top}px`,
                            left: `${modalPosition.left}px`
                        }}
                >
                    <div className="settingsModal-content">
                        {tableName === 'crude_main_delivery' && (
                            <div 
                                className={`settingsModal-item${hasActiveFilters ? ' settingsModal-item--disabled' : ''}`}
                                onClick={handleDownloadT1}
                            >
                                <div className="settingsModal-description">
                                    Download T1 Form
                                </div>
                            </div>
                        )}
                        {tableName === 'schedule_table' && (
                            <>
                                <div 
                                    className="settingsModal-item"
                                    onClick={handleOpenReportUploader}
                                >
                                    <div className="settingsModal-description">
                                        Upload Carrier Report
                                    </div>
                                </div>
                                <div 
                                    className="settingsModal-item"
                                    onClick={handleOpenPdfUploader}
                                >
                                    <div className="settingsModal-description">
                                        Upload PDF
                                    </div>
                                </div>
                                <div 
                                    className="settingsModal-item"
                                    onClick={() => {
                                        setShowSettingsModal(false);
                                        setShowAutomaticScheduling(true);
                                    }}
                                >
                                    <div className="settingsModal-description">
                                        Automatic Scheduling
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
            <ReportUploader ref={reportUploaderRef} />
            <PdfUploader ref={pdfUploaderRef} />
            {showAutomaticScheduling && (
                <AutomaticScheduling onClose={() => setShowAutomaticScheduling(false)} />
            )}
        </div>
    );
};

export default SettingsModal;